// Shared helper functions for challenge-related operations (DRY principle)
import { getTodayString, daysBetween } from './dateHelpers';

/**
 * Get display category name from challenge type
 */
export function getChallengeCategory(type: string | undefined | null): string {
  if (!type) return "Running"; // Default to Running (initial focus)

  const categoryMap: Record<string, string> = {
    run: "Running",
    walk: "Walking",
    cycle: "Cycling",
    strength: "Strength",
    yoga: "Yoga",
    cardio: "Cardio",
  };
  return categoryMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Get first sentence from text for preview display
 */
export function getFirstSentence(text: string | null | undefined): string {
  if (!text) return "Join and start your fitness journey!";

  // Match first sentence (ending with . ! or ?)
  const match = text.match(/^[^.!?]+[.!?]/);
  if (match) {
    return match[0].trim() + "..";
  }

  // If no sentence ending found, truncate at 60 chars
  if (text.length > 60) {
    return text.substring(0, 60).trim() + "...";
  }

  return text;
}

/**
 * Get icon name for challenge type
 */
export function getChallengeIcon(type: string | undefined | null): string {
  if (!type) return "directions_run"; // Default to running icon

  const iconMap: Record<string, string> = {
    run: "directions_run",
    walk: "directions_walk",
    cycle: "directions_bike",
    strength: "fitness_center",
    yoga: "self_improvement",
    cardio: "favorite",
  };
  return iconMap[type] || "directions_run";
}

/**
 * Calculate time left until end date
 * Uses date-only comparison (YYYY-MM-DD strings) for consistent behavior across timezones
 */
export function getTimeLeft(endDate: string | Date): string {
  const todayStr = getTodayString();
  const endDateStr = typeof endDate === "string"
    ? (endDate.includes("T") ? endDate.split("T")[0] : endDate)
    : `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")}`;

  // String comparison works for YYYY-MM-DD format
  if (endDateStr < todayStr) {
    return "Ended";
  }

  const daysLeft = daysBetween(todayStr, endDateStr);

  if (daysLeft === 0) return "Ends today";
  if (daysLeft === 1) return "1 day left";
  if (daysLeft < 7) return `${daysLeft} days left`;
  if (daysLeft < 30) {
    const weeks = Math.ceil(daysLeft / 7);
    return weeks === 1 ? "1 week left" : `${weeks} weeks left`;
  }
  const months = Math.ceil(daysLeft / 30);
  return months === 1 ? "1 month left" : `${months} months left`;
}

/**
 * Get status text for challenge
 * Note: Once we have activity tracking, this will be more dynamic
 */
export function getStatusText(
  status: string,
  progress?: number
): string {
  if (status === "completed") {
    return "Completed!";
  }

  if (status === "active") {
    if (progress !== undefined) {
      if (progress >= 80) return "Almost there!";
      if (progress >= 50) return "On Track!";
      if (progress >= 20) return "Keep going!";
      return "Get started!";
    }
    return "Active";
  }

  if (status === "pending") {
    return "Starting soon";
  }

  return "Unknown";
}

/**
 * Calculate challenge progress percentage based on days completed vs required days
 */
export function calculateProgress(challenge: {
  requiredDays: number;
  myProgress?: { daysCompleted?: number } | null;
}): number {
  const daysCompleted = challenge.myProgress?.daysCompleted ?? 0;
  const requiredDays = challenge.requiredDays;

  if (requiredDays <= 0) return 0;

  // Calculate percentage, capped at 100%
  const progress = Math.min(100, Math.round((daysCompleted / requiredDays) * 100));
  return progress;
}

/**
 * Check if a challenge was completed within the last N days
 * Used to show recently completed challenges on the Active tab
 *
 * Uses date-only comparison (YYYY-MM-DD) in user's local timezone to match
 * how the backend determines challenge completion.
 */
export function isRecentlyCompleted(
  endDate: string | Date,
  daysThreshold = 7
): boolean {
  // Get end date as YYYY-MM-DD string
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;
  const endDateStr = end.toISOString().split('T')[0]; // YYYY-MM-DD in UTC

  // Get user's local date as YYYY-MM-DD string
  const now = new Date();
  const userLocalDateStr = now.toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone

  // Parse both as dates for comparison (at midnight UTC for consistency)
  const endDateMidnight = new Date(endDateStr + 'T00:00:00.000Z');
  const userLocalMidnight = new Date(userLocalDateStr + 'T00:00:00.000Z');

  // Calculate difference in days
  const diffMs = userLocalMidnight.getTime() - endDateMidnight.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  // Challenge ended (user's local date >= end date) and within threshold
  return diffDays >= 0 && diffDays <= daysThreshold;
}

/**
 * Calculate time until challenge starts
 * Uses date-only comparison (YYYY-MM-DD strings) for consistent behavior across timezones
 */
export function getTimeUntilStart(startDate: string | Date): string {
  const todayStr = getTodayString();
  const startDateStr = typeof startDate === "string"
    ? (startDate.includes("T") ? startDate.split("T")[0] : startDate)
    : `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, "0")}-${String(startDate.getDate()).padStart(2, "0")}`;

  // String comparison works for YYYY-MM-DD format
  if (startDateStr <= todayStr) {
    return "Started";
  }

  const daysUntil = daysBetween(todayStr, startDateStr);

  if (daysUntil === 0) return "Starts today";
  if (daysUntil === 1) return "Starts tomorrow";
  if (daysUntil < 7) return `Starts in ${daysUntil} days`;
  if (daysUntil < 30) {
    const weeks = Math.ceil(daysUntil / 7);
    return weeks === 1 ? "Starts in 1 week" : `Starts in ${weeks} weeks`;
  }
  const months = Math.ceil(daysUntil / 30);
  return months === 1 ? "Starts in 1 month" : `Starts in ${months} months`;
}

/**
 * Placeholder images for challenge types
 * Uses existing mock images from mockHomeData
 */
export const CHALLENGE_TYPE_IMAGES: Record<string, string> = {
  run: "https://lh3.googleusercontent.com/aida-public/AB6AXuCUUlmNXYF9qAmmU_nx98mDzzeZPPDhwRc2fevsfpg3T0wqsQN7wYwnJFbRQEfaMcI9eH0uvKW7p6bd0xXVqw_PLa6xlwqc_EicRMS7ViXKaLKSozNevpY7pInt_I0irs4atKJJp-y6O3V_j3BB6PzGHF5urA19jSLrr06Ein726X1ePsxxy13CrscEKu-8-9FOKZztOS9_WmpolyhOdn3s174BtOvqurTpg9jZs5LEJUtG3RAoK14I7PrzYy9mvzeVfXUdrIFgM2oF",
  walk: "https://lh3.googleusercontent.com/aida-public/AB6AXuAlfyAz2YBxfF7cfMGfdRvGJHySMvJR8rpyU7bfS9rluMLEjPDSPtP_NG5uZLjPREtzI6kXQDzY0xw7WIGiEa5oj2BUlRmmzOZaOJGXoOsIWTAjznNow6voHbN6UZ9EE7Cw44rCVNktQYV26o5fLeJWcbJY3YTJ1SIRngrPVKoG0V9BFUNzNS4-Iuf8RBpDNow2-rSHNF0q6Dax52AxGYet2Y1wA-FCIKhs0X-ux8rbUGgkvJtNB70gSexEYuySybc39KmIbuS95DBf",
  cycle: "https://lh3.googleusercontent.com/aida-public/AB6AXuCiu1yP2XcvOWGXbvyeB7MY3D198wxUQJQ5Q8_uuVdUNrcXkmlrOOecXFyUqSJ5BD_v1-MsH2bPXne3nGoIrU9ZiQHJ3DWBr3lnBGpZRv9w2PI27VlmavpXZTKWRH4ZIUtIZm3C63fnyxF03C3ZwhusKFGNX5m5ZzvAaOGQ2ues77j3ZFbEKukV4kLHmZuYZYnNsDgvsAmcWuNx14at0krmbzzGdX8BlyRb6tt_qDPOJqH5iscjoqomYd0sUbtxKS9MSlu5xa1kC9_V",
};

/**
 * Get placeholder image URL based on challenge type
 */
export function getChallengeImage(type: string | undefined | null): string {
  if (!type) return CHALLENGE_TYPE_IMAGES.run;
  return CHALLENGE_TYPE_IMAGES[type.toLowerCase()] || CHALLENGE_TYPE_IMAGES.run;
}

/**
 * Challenge joinability status - single source of truth
 * Used by ChallengePreview and Challenge pages to determine if user can join
 */
export interface ChallengeJoinability {
  canJoin: boolean;
  reason:
    | "joinable"
    | "already_joined"
    | "already_started"
    | "already_ended"
    | "cancelled"
    | "completed";
  message: string;
  buttonText: string;
  badgeText?: string;
  badgeVariant?: "success" | "warning" | "error" | "info";
}

/**
 * Determine if a user can join a challenge based on dates, status, and participation
 * This is the single source of truth for join eligibility logic
 *
 * Uses date-only comparison (YYYY-MM-DD strings) to avoid timezone issues.
 * "December 21" means December 21 everywhere, regardless of user's timezone.
 */
export function getChallengeJoinability(
  challenge: {
    startDate: string | Date;
    endDate: string | Date;
    status: string;
    stakeAmount: number;
  },
  hasUserJoined: boolean
): ChallengeJoinability {
  const todayStr = getTodayString();

  // Convert dates to YYYY-MM-DD strings for comparison
  const startDateStr = typeof challenge.startDate === "string"
    ? (challenge.startDate.includes("T") ? challenge.startDate.split("T")[0] : challenge.startDate)
    : `${challenge.startDate.getFullYear()}-${String(challenge.startDate.getMonth() + 1).padStart(2, "0")}-${String(challenge.startDate.getDate()).padStart(2, "0")}`;

  const endDateStr = typeof challenge.endDate === "string"
    ? (challenge.endDate.includes("T") ? challenge.endDate.split("T")[0] : challenge.endDate)
    : `${challenge.endDate.getFullYear()}-${String(challenge.endDate.getMonth() + 1).padStart(2, "0")}-${String(challenge.endDate.getDate()).padStart(2, "0")}`;

  // Priority order matters - check most restrictive first

  if (hasUserJoined) {
    return {
      canJoin: false,
      reason: "already_joined",
      message: "You have already joined this challenge",
      buttonText: "Already Joined",
      badgeText: "Joined",
      badgeVariant: "success",
    };
  }

  if (challenge.status === "cancelled") {
    return {
      canJoin: false,
      reason: "cancelled",
      message: "This challenge has been cancelled",
      buttonText: "Challenge Cancelled",
      badgeText: "Cancelled",
      badgeVariant: "error",
    };
  }

  // String comparison works for YYYY-MM-DD format (lexicographic = chronological)
  if (challenge.status === "completed" || todayStr > endDateStr) {
    return {
      canJoin: false,
      reason: "already_ended",
      message: "This challenge has already ended",
      buttonText: "Challenge Ended",
      badgeText: "Ended",
      badgeVariant: "error",
    };
  }

  if (todayStr >= startDateStr) {
    return {
      canJoin: false,
      reason: "already_started",
      message: "This challenge has already started",
      buttonText: "Already Started",
      badgeText: "In Progress",
      badgeVariant: "warning",
    };
  }

  // Joinable - challenge hasn't started yet
  return {
    canJoin: true,
    reason: "joinable",
    message: "You can join this challenge",
    buttonText: `Join Challenge - Stake $${challenge.stakeAmount}`,
    badgeText: getTimeUntilStart(startDateStr),
    badgeVariant: "info",
  };
}
